/* eslint-disable max-lines */
import { IconNames } from '@blueprintjs/icons';
import { BenefitTypeOptions, RouteCodeDisplayTextMap } from '@strata/constants';
import { conditionalString, formatDate, sortAsc } from '@strata/helper-functions';
import { ActionButtonText, ToggleButton } from '@strata/react-buttons';
import { GridWithReordering, PrimaryData, SecondaryData } from '@strata/react-grid';
import { SuggestSingleSelect } from '@strata/react-selects';
import { Icon } from '@strata/react-status-icons';
import { SuccessToast } from '@strata/react-toast-message';
import classNames from 'classnames';
import { Form, Formik } from 'formik';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import * as Yup from 'yup';

import { ListPageHeader } from 'components/ListPageHeader';
import { DeleteRouteRuleDialog } from 'pages/routeManagement/dialogs/DeleteRouteRuleDialog';
import { DuplicateRouteRulesDialog } from 'pages/routeManagement/dialogs/DuplicateRouteRulesDialog';
import { RouteStatusIndicator } from 'pages/routeManagement/RouteStatusIndicator';
import { CREATE_ROUTE } from 'services/constants';
import { handleError } from 'services/ErrorManagement';
import { put, useGet } from 'services/Http';
import { mapPayerSegment } from 'services/PayerSegmentSupport';
import { getRoutes, reorderRoutes, RouteGroup, routeRulesUrl } from 'services/RouteRuleHelpers';

const isRouteInactive = (route) =>
    RouteStatusIndicator.getStatus({
        isActive: route.is_active,
        startDate: route.start_date,
        endDate: route.end_date,
    }).message === RouteStatusIndicator.Statuses.INACTIVE;

const getDisplayRoutes = (routes, hideInactive) => {
    if (!hideInactive) {
        return routes;
    }
    return routes?.map((group) => ({
        ...group,
        droppable: false, // Disable reordering when inactive routes are hidden
        data: group.data?.filter((route) => !isRouteInactive(route)),
    }));
};

const columns = ({ segmentId, benefit, setRouteRuleToDelete, navigate }) => [
    {
        header: '#',
        accessorKey: 'display_sequence',
        size: 40,
        cell: ({ getValue, row }) => {
            if (row.original.isPostLiveReferralAssignment) {
                return (
                    <ActionButtonText
                        label="View Assignments"
                        rightIcon={IconNames.DOUBLE_CHEVRON_RIGHT}
                        onClick={() =>
                            navigate(`/route-management/post-live-referral-assignment/${segmentId}`)
                        }
                    />
                );
            }
            return <PrimaryData data={getValue()} />;
        },
    },
    {
        header: 'Route Rule Name',
        accessorKey: 'route_name',
        size: 200,
        cell: ({ getValue, row }) =>
            row.original.isPostLiveReferralAssignment ? null : (
                <span className="flex__gap--small">
                    {row.original.is_global && <Icon icon={IconNames.LINK} title="Global Route" />}
                    <PrimaryData data={getValue()} />
                </span>
            ),
    },
    {
        header: 'Description',
        accessorKey: 'route_description',
        size: 150,
        cell: ({ getValue, row }) =>
            row.original.isPostLiveReferralAssignment ? null : <SecondaryData data={getValue()} />,
    },
    {
        header: 'Route Code',
        accessorKey: 'route_code',
        size: 100,
        cell: ({ getValue, row }) =>
            row.original.isPostLiveReferralAssignment ? null : (
                <SecondaryData data={RouteCodeDisplayTextMap[getValue()]} />
            ),
    },
    {
        header: 'Start Date',
        accessorKey: 'start_date',
        size: 100,
        cell: ({ getValue, row }) =>
            row.original.isPostLiveReferralAssignment ? null : (
                <SecondaryData data={formatDate(getValue())} />
            ),
    },
    {
        header: 'End Date',
        accessorKey: 'end_date',
        size: 100,
        cell: ({ getValue, row }) =>
            row.original.isPostLiveReferralAssignment ? null : (
                <SecondaryData data={formatDate(getValue())} />
            ),
    },
    {
        header: 'Status',
        accessorKey: 'is_active',
        size: 100,
        cell: ({ getValue, row }) =>
            row.original.isPostLiveReferralAssignment ? null : (
                <RouteStatusIndicator
                    isActive={getValue()}
                    startDate={row.original.start_date}
                    endDate={row.original.end_date}
                />
            ),
    },
    {
        header: 'Actions',
        accessorKey: 'route_rule_id',
        size: 70,
        cell: ({ getValue, row }) => (
            <div className="sds_filter-list__actions">
                {/* Global routes are configured on the Global Routes page */}
                {row.original.can_edit !== false && !row.original.is_global && (
                    <Icon
                        icon={IconNames.EDIT}
                        intent={Icon.Intent.SECONDARY}
                        title="Edit"
                        onClick={() =>
                            navigate(
                                `${routeRulesUrl(segmentId, benefit)}${conditionalString(
                                    ![
                                        RouteGroup.BEFORE_CONTINUITY,
                                        RouteGroup.AFTER_CONTINUITY,
                                    ].includes(row.original.route_group),
                                    '/continuity-of-care',
                                )}/${getValue()}`,
                            )
                        }
                    />
                )}
                {row.original.can_delete !== false && (
                    <Icon
                        icon={row.original.is_global ? IconNames.CROSS : IconNames.TRASH}
                        intent={Icon.Intent.SECONDARY}
                        title={row.original.is_global ? 'Remove' : 'Delete'}
                        onClick={() => setRouteRuleToDelete(row.original)}
                    />
                )}
            </div>
        ),
    },
];

export const RouteRules = () => {
    const { segmentId, benefit } = useParams();
    const navigate = useNavigate();

    const payerSegments = useGet('/reference/payer_segments');
    const payerSegmentOptions = useMemo(
        () => payerSegments?.map(mapPayerSegment).sort(({ name: a }, { name: b }) => sortAsc(a, b)),
        [payerSegments],
    );
    const payerSegment = useMemo(
        () => payerSegmentOptions?.find(({ secondaryData }) => secondaryData === segmentId),
        [payerSegmentOptions, segmentId],
    );
    const payerId = payerSegment?.payer_id;

    const [routes, setRoutes] = useState();
    const loadRoutes = useCallback(() => {
        if (!payerId || !segmentId || !benefit) {
            return;
        }
        void (async () => {
            try {
                setRoutes(await getRoutes(payerId, segmentId, benefit));
            } catch (err) {
                handleError('Error fetching routes', err);
            }
        })();
    }, [payerId, segmentId, benefit]);

    useEffect(() => {
        loadRoutes();
        return () => {
            setRoutes();
        };
    }, [loadRoutes]);

    /** Clear the list so it shows its loading state while the routes are fetched again */
    const reloadRoutes = () => {
        setRoutes();
        loadRoutes();
    };

    const [routeRuleToDelete, setRouteRuleToDelete] = useState();
    const [showDuplicateRouteRulesDialog, setShowDuplicateRouteRulesDialog] = useState(false);
    const [hideInactiveRoutes, setHideInactiveRoutes] = useState(true);

    return (
        <div className="sds_list-page">
            <div className="sds_list-page__content--top">
                <SuggestSingleSelect
                    id="segmentId"
                    name="segmentId"
                    label="Payer Segment"
                    required
                    isLoading={!payerSegments}
                    values={payerSegmentOptions}
                    onChange={(newValue) =>
                        navigate(routeRulesUrl(newValue?.secondaryData, benefit))
                    }
                    selectedItem={payerSegmentOptions?.find(
                        (segment) => segment.secondaryData === segmentId,
                    )}
                />
                <SuggestSingleSelect
                    id="benefit"
                    name="benefit"
                    label="Benefit"
                    required
                    values={BenefitTypeOptions}
                    onChange={(newValue) => navigate(routeRulesUrl(segmentId, newValue?.value))}
                    selectedItem={BenefitTypeOptions.find(({ value }) => value === benefit)}
                />
            </div>
            {segmentId && benefit && (
                <Formik
                    initialValues={{ routes }}
                    enableReinitialize
                    validationSchema={Yup.object({
                        routes: Yup.array().required(),
                    })}
                    onSubmit={async (values) => {
                        try {
                            setRoutes();

                            // Only send custom configured routes
                            const body = values.routes
                                .filter((_, i) =>
                                    [
                                        RouteGroup.BEFORE_CONTINUITY,
                                        RouteGroup.AFTER_CONTINUITY,
                                    ].includes(i),
                                )
                                .map((group) =>
                                    group.data.map((route) => ({
                                        route_rule_id: route.route_rule_id,
                                        route_sequence: route.route_sequence,
                                        before_continuity: route.before_continuity,
                                    })),
                                )
                                .flat();

                            await put(
                                `/clients/admin/${payerId}/${segmentId}/route_rules/${benefit}`,
                                body,
                            );

                            SuccessToast({ message: 'Reorder saved successfully.' });
                            setRoutes(await getRoutes(payerId, segmentId, benefit));
                        } catch (err) {
                            handleError('Error saving reorder. Please try again.', err);
                            setRoutes(values.routes);
                        }
                    }}
                >
                    {(formik) => (
                        <Form className="sds_main-content__container">
                            <DeleteRouteRuleDialog
                                payerId={payerId}
                                segmentId={segmentId}
                                benefit={benefit}
                                routeRuleToDelete={routeRuleToDelete}
                                onDeleteSuccess={reloadRoutes}
                                onClose={() => setRouteRuleToDelete()}
                            />
                            <DuplicateRouteRulesDialog
                                isOpen={showDuplicateRouteRulesDialog}
                                payerId={payerId}
                                segmentId={segmentId}
                                segmentName={payerSegment?.name}
                                benefit={benefit}
                                onClose={() => setShowDuplicateRouteRulesDialog(false)}
                            />
                            <ListPageHeader
                                pageTitle="Route Rules"
                                rightContent={
                                    <>
                                        <ActionButtonText
                                            leftIcon={IconNames.REFRESH}
                                            label="Refresh"
                                            disabled={!formik.values.routes}
                                            onClick={reloadRoutes}
                                        />
                                        <ToggleButton
                                            label="Hide Inactive Routes"
                                            isOn={hideInactiveRoutes}
                                            onChange={(e) =>
                                                setHideInactiveRoutes(e.currentTarget.checked)
                                            }
                                        />
                                        <ActionButtonText
                                            leftIcon={IconNames.DUPLICATE}
                                            label="Duplicate Rules To Another Payer"
                                            onClick={() => setShowDuplicateRouteRulesDialog(true)}
                                        />
                                    </>
                                }
                                buttons={[
                                    {
                                        label: 'Save Reorder',
                                        type: 'submit',
                                        disabled:
                                            !(formik.dirty && formik.isValid) ||
                                            formik.isSubmitting,
                                    },
                                    {
                                        label: 'Create Route',
                                        path: `${routeRulesUrl(
                                            segmentId,
                                            benefit,
                                        )}/${CREATE_ROUTE}`,
                                    },
                                ]}
                            />
                            <div className="sds_main-content__grid">
                                <GridWithReordering
                                    dataGroups={getDisplayRoutes(
                                        formik.values.routes,
                                        hideInactiveRoutes,
                                    )}
                                    isLoading={!formik.values.routes}
                                    columns={columns({
                                        segmentId,
                                        benefit,
                                        setRouteRuleToDelete,
                                        navigate,
                                    })}
                                    onReorder={(dataGroups) =>
                                        formik.setFieldValue('routes', reorderRoutes(dataGroups))
                                    }
                                    rowCssClass={(rowData) =>
                                        classNames({
                                            'griddle-row--disabled':
                                                RouteStatusIndicator.getStatus({
                                                    isActive: rowData.is_active,
                                                    startDate: rowData.start_date,
                                                    endDate: rowData.end_date,
                                                }).message ===
                                                RouteStatusIndicator.Statuses.INACTIVE,
                                        })
                                    }
                                />
                            </div>
                        </Form>
                    )}
                </Formik>
            )}
        </div>
    );
};
